import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { 
  GraduationCap, 
  Mic,
  MicOff,
  Video,
  VideoOff,
  Clock,
  CheckCircle2,
  Loader2,
  Play,
  Square,
  Volume2,
  AlertTriangle,
  AlertCircle,
  Eye,
  Users,
  Shield,
  Waves,
  Star,
  Send,
  MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import * as faceapi from 'face-api.js';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Compact Audio Visualizer Component - More delicate design
function AudioVisualizer({ isActive }) {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const barsRef = useRef(Array(20).fill(0).map(() => Math.random() * 0.2));

  useEffect(() => {
    if (!isActive || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const bars = barsRef.current;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      const barWidth = canvas.width / bars.length;

      bars.forEach((height, i) => {
        const targetHeight = isActive ? Math.random() * 0.7 + 0.3 : 0.1;
        bars[i] += (targetHeight - bars[i]) * 0.12;
        
        const barHeight = bars[i] * canvas.height;
        const x = i * barWidth;
        const y = (canvas.height - barHeight) / 2;

        // Create gradient for each bar
        const gradient = ctx.createLinearGradient(x, y + barHeight, x, y);
        gradient.addColorStop(0, 'rgba(132, 204, 22, 0.6)');
        gradient.addColorStop(1, 'rgba(34, 211, 238, 0.8)');
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.roundRect(x + 1, y, barWidth - 2, barHeight, 2);
        ctx.fill();
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isActive]);

  return (
    <canvas 
      ref={canvasRef} 
      width={160} 
      height={32}
      className="opacity-80"
    />
  );
}

// Warning Toast Component
function WarningOverlay({ message, type }) {
  if (!message) return null;
  
  return (
    <div className={`fixed bottom-20 left-1/2 transform -translate-x-1/2 z-50 
      px-4 py-2 rounded-lg backdrop-blur-md border text-sm
      ${type === 'critical' ? 'bg-red-500/30 border-red-500/50 text-red-200' : 'bg-yellow-500/30 border-yellow-500/50 text-yellow-200'}`}>
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
        <span className="font-medium text-xs">{message}</span>
      </div>
    </div>
  );
}

export default function InterviewRoom() {
  const { token } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const fullRecorderRef = useRef(null);
  const fullChunksRef = useRef([]);
  const speechSynthRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const referenceFaceDescriptorRef = useRef(null);
  const referenceFaceImageRef = useRef(null); // Store reference face image for AWS comparison
  const awsFaceSwapIntervalRef = useRef(null); // Interval for AWS face swap detection
  const proctoringIntervalRef = useRef(null);
  const speechRecognitionRef = useRef(null);
  const transcriptRef = useRef('');
  
  // Core states
  const [loading, setLoading] = useState(true);
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [questions, setQuestions] = useState([]);
  const questionsRef = useRef([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const currentQuestionIndexRef = useRef(0);
  const activeQuestionIdRef = useRef(null); // Captures the question ID when question starts
  const [phase, setPhase] = useState('mic-test'); // mic-test -> countdown -> reading -> answering -> submitted -> (loop or complete)
  const [timeLeft, setTimeLeft] = useState(0);
  const [totalTimeLeft, setTotalTimeLeft] = useState(null); // Will be set from settings
  const [isRecording, setIsRecording] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraError, setCameraError] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const recordedChunksRef = useRef([]);
  const [submitting, setSubmitting] = useState(false);
  const [interviewComplete, setInterviewComplete] = useState(false);
  const [results, setResults] = useState(null);
  const [universityName, setUniversityName] = useState('');
  const [isFullRecording, setIsFullRecording] = useState(false);
  const [uploadingRecording, setUploadingRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [settings, setSettings] = useState({ enable_tts: true, tts_voice: 'female' });
  const [currentTranscript, setCurrentTranscript] = useState('');
  
  // Countdown and Feedback states
  const [countdownSeconds, setCountdownSeconds] = useState(5);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  
  // Mic Test states
  const [micTestPhase, setMicTestPhase] = useState('pending'); // pending, testing, passed, failed
  const [micTestTranscript, setMicTestTranscript] = useState('');
  const [micTestTimer, setMicTestTimer] = useState(10);
  
  // Proctoring states
  const [warningMessage, setWarningMessage] = useState('');
  const [warningType, setWarningType] = useState('warning');
  const [proctoring, setProctoring] = useState({
    tabSwitchCount: 0,
    eyeContactPercent: 0, // Start at 0 - will be calculated from actual detections
    eyeContactSamples: 0,
    eyeContactPositive: 0,
    multipleFacesCount: 0,
    lastMultipleFacesTime: 0, // Track when multiple faces were last detected
    faceMatchPercent: 0, // Start at 0 - will be calculated if reference face exists
    faceMatchSamples: 0,
    faceSwapEvents: 0, // Count of times face didn't match reference (face-api.js)
    awsFaceSwapEvents: 0, // Count of AWS Rekognition face swap detections
    awsFaceSwapLastCheck: null, // Last AWS check timestamp
    lastFaceDistance: 0, // Last face comparison distance
    voiceActivityPercent: 0,
    multipleVoicesCount: 0, // Count of times multiple voices were detected
    dominantFrequency: 0, // Current dominant voice frequency
    suspiciousPatterns: 0,
    livenessScore: 100,
    blinkCount: 0,
    lastBlinkTime: 0,
    naturalToneScore: 100,
    audioLevelHistory: [],
    faceAbsentCount: 0,
    faceAbsentDuration: 0,
    consecutiveFaceMisses: 0, // Track consecutive frames without face detection
    // Per-question eye contact tracking
    currentQuestionEyeContact: { samples: 0, positive: 0, percent: 0 },
    questionsEyeContact: [], // Array of {questionIndex, percent} for each question
    // Network connectivity tracking
    networkSamples: [],
    networkScore: 100,
  });
  
  // Eye contact guide visibility
  const [showEyeGuide, setShowEyeGuide] = useState(true);
  
  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenExitCount, setFullscreenExitCount] = useState(0);
  const [showFullscreenPrompt, setShowFullscreenPrompt] = useState(false);
  const containerRef = useRef(null);

  // Initialize Speech Recognition
  const [speechStatus, setSpeechStatus] = useState('inactive'); // inactive, listening, error
  const [interimTranscript, setInterimTranscript] = useState('');
  const isRecordingRef = useRef(false);
  const phaseRef = useRef('mic-test');
  
  // Keep refs in sync with state for use in callbacks
  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);
  
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  // Fullscreen management
  const enterFullscreen = useCallback(async () => {
    try {
      const elem = containerRef.current || document.documentElement;
      if (elem.requestFullscreen) {
        await elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        await elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        await elem.msRequestFullscreen();
      }
      setIsFullscreen(true);
      setShowFullscreenPrompt(false);
    } catch (err) {
      console.error('Failed to enter fullscreen:', err);
      toast.error('Could not enter fullscreen mode. Please allow fullscreen permission.');
    }
  }, []);

  const exitFullscreen = useCallback(() => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }
  }, []);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isNowFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      );
      
      setIsFullscreen(isNowFullscreen);
      
      // If user exited fullscreen during interview (not mic-test), track it and show prompt
      if (!isNowFullscreen && phase !== 'mic-test' && phase !== 'complete') {
        setFullscreenExitCount(prev => {
          const newCount = prev + 1;
          // Log proctoring event
          setProctoring(p => ({
            ...p,
            suspiciousPatterns: p.suspiciousPatterns + 1
          }));
          console.log(`[Proctoring] Fullscreen exited - count: ${newCount}`);
          return newCount;
        });
        setShowFullscreenPrompt(true);
        toast.warning('Please return to fullscreen mode to continue the interview.');
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, [phase]);

  // Auto-enter fullscreen when interview starts (after mic test)
  useEffect(() => {
    if (phase === 'countdown' && !isFullscreen) {
      enterFullscreen();
    }
  }, [phase, isFullscreen, enterFullscreen]);

  // Initialize speech recognition ONCE on mount
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      recognition.maxAlternatives = 3; // Increased from 1 for better accuracy
      
      // Track recognition state for better restart logic
      let recognitionActive = false;
      let lastResultTime = Date.now();
      let missedSpeechBuffer = '';
      
      recognition.onstart = () => {
        console.log('Speech recognition started');
        recognitionActive = true;
        setSpeechStatus('listening');
        lastResultTime = Date.now();
      };
      
      recognition.onresult = (event) => {
        let finalTranscript = '';
        let interim = '';
        lastResultTime = Date.now();
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          // Get the most confident result
          let bestTranscript = result[0].transcript;
          let bestConfidence = result[0].confidence || 0;
          
          // Check alternative results for potentially better matches
          for (let j = 1; j < result.length; j++) {
            if (result[j].confidence > bestConfidence) {
              bestTranscript = result[j].transcript;
              bestConfidence = result[j].confidence;
            }
          }
          
          if (result.isFinal) {
            finalTranscript += bestTranscript + ' ';
            console.log('Final transcript:', bestTranscript, 'confidence:', (bestConfidence * 100).toFixed(1) + '%');
          } else {
            interim += bestTranscript;
          }
        }
        
        // Show interim results in real-time
        setInterimTranscript(interim);
        
        if (finalTranscript) {
          // Include any buffered missed speech
          if (missedSpeechBuffer) {
            finalTranscript = missedSpeechBuffer + ' ' + finalTranscript;
            missedSpeechBuffer = '';
          }
          transcriptRef.current += finalTranscript;
          setCurrentTranscript(transcriptRef.current);
          setInterimTranscript(''); // Clear interim when we get final
        }
      };
      
      recognition.onerror = (event) => {
        console.log('Speech recognition error:', event.error);
        recognitionActive = false;
        
        // Handle different error types
        if (event.error === 'no-speech') {
          // No speech detected - this is normal during pauses
          // Check if there might be missed speech (long gap)
          const timeSinceLastResult = Date.now() - lastResultTime;
          if (timeSinceLastResult > 5000) {
            console.log('Long gap without speech detected, may have missed content');
          }
          
          // Restart if still recording
          setTimeout(() => {
            try {
              if (isRecordingRef.current && phaseRef.current === 'answering') {
                recognition.start();
              }
            } catch (e) {
              console.log('Restart after no-speech failed:', e);
            }
          }, 50); // Reduced delay for faster restart
        } else if (event.error === 'aborted') {
          // Recognition was aborted - restart quickly if still in answering phase
          setTimeout(() => {
            try {
              if (isRecordingRef.current && phaseRef.current === 'answering') {
                recognition.start();
              }
            } catch (e) {}
          }, 50);
        } else if (event.error === 'network') {
          // Network error - may have lost some speech, try to recover
          console.warn('Network error during speech recognition - some speech may be lost');
          setSpeechStatus('error');
          toast.warning('Network issue detected. Some speech may not be transcribed.');
          
          // Retry after a short delay
          setTimeout(() => {
            try {
              if (isRecordingRef.current && phaseRef.current === 'answering') {
                recognition.start();
              }
            } catch (e) {}
          }, 500);
        } else if (event.error === 'audio-capture') {
          console.error('Microphone not available');
          setSpeechStatus('error');
          toast.error('Microphone not detected. Please check your mic settings.');
        } else if (event.error === 'not-allowed') {
          console.error('Microphone permission denied');
          setSpeechStatus('error');
          toast.error('Microphone permission denied. Please allow microphone access.');
        } else if (event.error === 'service-not-allowed') {
          console.error('Speech service not allowed');
          setSpeechStatus('error');
          toast.error('Speech recognition service unavailable. Please try again.');
        }
      };
      
      recognition.onend = () => {
        console.log('Speech recognition ended, isRecording:', isRecordingRef.current, 'phase:', phaseRef.current, 'transcript length:', transcriptRef.current.length);
        recognitionActive = false;
        setSpeechStatus('inactive');
        
        // Auto-restart ONLY if still actively recording in answering phase
        // The isRecordingRef check is critical - it prevents restart when we intentionally stop
        if (isRecordingRef.current && phaseRef.current === 'answering' && speechRecognitionRef.current) {
          console.log('Auto-restarting speech recognition...');
          // Shorter delay for faster restart to minimize missed speech
          setTimeout(() => {
            try {
              // Double-check we're still recording before restarting
              if (isRecordingRef.current && phaseRef.current === 'answering' && !recognitionActive) {
                recognition.start();
                console.log('Speech recognition restarted');
              }
            } catch (e) {
              console.log('Failed to restart recognition:', e);
              // Try again after a short delay only if still recording
              setTimeout(() => {
                try {
                  if (isRecordingRef.current && phaseRef.current === 'answering') {
                    recognition.start();
                  }
                } catch (e2) {}
              }, 200);
            }
          }, 50); // Reduced from 100ms to 50ms for faster restart
        }
      };
      
      // Add periodic check to ensure recognition is running
      const checkRecognitionInterval = setInterval(() => {
        if (isRecordingRef.current && phaseRef.current === 'answering' && !recognitionActive) {
          console.log('Recognition check: Not active, attempting restart...');
          try {
            recognition.start();
          } catch (e) {
            // Already running or other error
          }
        }
      }, 2000); // Check every 2 seconds
      
      speechRecognitionRef.current = recognition;
      console.log('Speech recognition initialized with enhanced settings');
      
      return () => {
        clearInterval(checkRecognitionInterval);
      };
    } else {
      console.error('Speech Recognition not supported in this browser');
      toast.error('Speech recognition not supported. Please use Google Chrome.');
    }
    
    return () => {
      if (speechRecognitionRef.current) {
        try {
          speechRecognitionRef.current.stop();
        } catch (e) {}
      }
    };
  }, []); // Empty dependency - initialize only once

  // Load face-api models with CDN fallback
  useEffect(() => {
    const loadModels = async () => {
      // Multiple model sources in order of preference
      const modelSources = [
        // 1. Official face-api.js GitHub CDN (jsDelivr) - most reliable
        'https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js/weights',
        // 2. Local models fallback
        '/models'
      ];
      
      for (let i = 0; i < modelSources.length; i++) {
        const modelPath = modelSources[i];
        console.log(`[InterviewRoom] Attempting to load face-api models from: ${modelPath} (source ${i + 1}/${modelSources.length})`);
        
        try {
          // First verify the models are accessible
          const testFetch = await fetch(`${modelPath}/tiny_face_detector_model-weights_manifest.json`);
          if (!testFetch.ok) {
            throw new Error(`Models not accessible: ${testFetch.status}`);
          }
          console.log('[InterviewRoom] Model manifest accessible, loading models...');
          
          // Load models sequentially to better handle errors
          await faceapi.nets.tinyFaceDetector.loadFromUri(modelPath);
          console.log('[InterviewRoom] TinyFaceDetector loaded');
          
          await faceapi.nets.faceLandmark68Net.loadFromUri(modelPath);
          console.log('[InterviewRoom] FaceLandmark68Net loaded');
          
          await faceapi.nets.faceRecognitionNet.loadFromUri(modelPath);
          console.log('[InterviewRoom] FaceRecognitionNet loaded');
          
          setModelsLoaded(true);
          console.log('[InterviewRoom] All face detection models loaded successfully from:', modelPath);
          return; // Success - exit the function
        } catch (err) {
          console.error(`[InterviewRoom] Failed to load models from ${modelPath}:`, err.message || err);
          
          if (i < modelSources.length - 1) {
            console.log('[InterviewRoom] Trying next model source...');
          }
        }
      }
      
      // All sources failed - set loaded anyway to not block interview but log warning
      console.error('[InterviewRoom] All model sources failed. Eye contact detection will not work.');
      setModelsLoaded(true);
    };
    
    // Small delay to ensure page is fully loaded
    const timer = setTimeout(() => loadModels(), 300);
    return () => clearTimeout(timer);
  }, []);

  // Tab visibility detection — uses document.hidden only to avoid false positives
  // from OS notifications, WhatsApp popups, etc. that fire window.blur without an actual tab switch
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && phase !== 'ready' && !interviewComplete) {
        setProctoring(prev => ({ ...prev, tabSwitchCount: prev.tabSwitchCount + 1 }));
        showWarning('Tab switch detected! Please stay on this page.', 'critical');
        logProctoringEvent('tab_switch');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [phase, interviewComplete]);

  // Network connectivity monitoring — sample every 30 seconds
  useEffect(() => {
    if (phase === 'ready' || interviewComplete) return;
    
    const measureNetwork = () => {
      const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      let score = 100;
      let quality = 'excellent';
      
      if (conn) {
        const rtt = conn.rtt || 0; // Round-trip time in ms
        const downlink = conn.downlink || 10; // Mbps
        const effectiveType = conn.effectiveType || '4g';
        
        // Score based on RTT and downlink
        if (rtt > 500 || downlink < 0.5 || effectiveType === 'slow-2g') { score = 20; quality = 'poor'; }
        else if (rtt > 300 || downlink < 1 || effectiveType === '2g') { score = 40; quality = 'fair'; }
        else if (rtt > 150 || downlink < 2 || effectiveType === '3g') { score = 65; quality = 'good'; }
        else if (rtt > 50 || downlink < 5) { score = 85; quality = 'very good'; }
      }
      
      setProctoring(prev => {
        const samples = [...(prev.networkSamples || []), score].slice(-20); // Keep last 20
        const avgScore = Math.round(samples.reduce((a, b) => a + b, 0) / samples.length);
        return { ...prev, networkSamples: samples, networkScore: avgScore };
      });
    };
    
    measureNetwork(); // Initial measurement
    const interval = setInterval(measureNetwork, 30000); // Every 30s
    return () => clearInterval(interval);
  }, [phase, interviewComplete]);

  // Total interview timer
  useEffect(() => {
    if (totalTimeLeft === null) return; // Wait for settings to load
    if (phase !== 'ready' && !interviewComplete && totalTimeLeft > 0) {
      const timer = setTimeout(() => setTotalTimeLeft(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    } else if (totalTimeLeft === 0 && !interviewComplete) {
      toast.error('Interview time limit reached');
      completeInterview();
    }
  }, [totalTimeLeft, phase, interviewComplete]);

  // Question timer
  useEffect(() => {
    if (timeLeft > 0 && (phase === 'reading' || phase === 'answering')) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && phase === 'reading') {
      startAnswering();
    } else if (timeLeft === 0 && phase === 'answering') {
      stopRecording();
    }
  }, [timeLeft, phase]);

  // Countdown timer effect - starts interview automatically after 5 seconds
  useEffect(() => {
    if (phase === 'countdown' && countdownSeconds > 0 && !loading) {
      const timer = setTimeout(() => {
        setCountdownSeconds(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (phase === 'countdown' && countdownSeconds === 0) {
      // Countdown finished, set phase to trigger question start
      setPhase('starting');
    }
  }, [phase, countdownSeconds, loading]);

  // Mic test timer
  useEffect(() => {
    if (phase === 'mic-test' && micTestPhase === 'testing' && micTestTimer > 0) {
      const timer = setTimeout(() => {
        setMicTestTimer(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (phase === 'mic-test' && micTestPhase === 'testing' && micTestTimer === 0) {
      // Time's up - check if we got any transcript
      stopMicTest();
    }
  }, [phase, micTestPhase, micTestTimer]);

  const startMicTest = () => {
    setMicTestPhase('testing');
    setMicTestTimer(10);
    setMicTestTranscript('');
    
    // Start speech recognition for mic test
    if (speechRecognitionRef.current) {
      try {
        speechRecognitionRef.current.start();
        console.log('Mic test started');
      } catch (e) {
        console.log('Speech recognition error:', e);
        setMicTestPhase('failed');
        toast.error('Failed to access microphone. Please check permissions.');
      }
    } else {
      setMicTestPhase('failed');
      toast.error('Speech recognition not supported in this browser. Please use Google Chrome.');
    }
  };

  const stopMicTest = () => {
    if (speechRecognitionRef.current) {
      try {
        speechRecognitionRef.current.stop();
      } catch (e) {}
    }
    
    // Check if we captured any speech
    const transcript = transcriptRef.current.trim();
    if (transcript.length > 0) {
      setMicTestPhase('passed');
      setMicTestTranscript(transcript);
      toast.success('Microphone test passed! Your voice was detected.');
    } else {
      setMicTestPhase('failed');
      toast.error('No speech detected. Please check your microphone.');
    }
    
    // Clear the transcript for the actual interview
    transcriptRef.current = '';
    setCurrentTranscript('');
    setInterimTranscript('');
    setSpeechStatus('inactive');
  };

  const skipMicTest = () => {
    // Stop any running recognition
    if (speechRecognitionRef.current) {
      try {
        speechRecognitionRef.current.stop();
      } catch (e) {}
    }
    // Allow skipping but warn user
    setPhase('countdown');
    toast.warning('Microphone test skipped. Transcription may not work properly.');
  };

  const proceedToInterview = () => {
    // Stop any running recognition from mic test
    if (speechRecognitionRef.current) {
      try {
        speechRecognitionRef.current.stop();
      } catch (e) {}
    }
    // Clear any test transcript
    transcriptRef.current = '';
    setCurrentTranscript('');
    setInterimTranscript('');
    setSpeechStatus('inactive');
    setPhase('countdown');
  };

  // Auto-advance after submission - automatically start next question
  useEffect(() => {
    console.log(`[AUTO-ADVANCE CHECK] phase=${phase}, submitting=${submitting}, complete=${interviewComplete}, idx=${currentQuestionIndex}, qLen=${questions.length}`);
    if (phase === 'submitted' && !submitting && !interviewComplete) {
      const isLastQuestion = currentQuestionIndex >= questions.length - 1;
      console.log(`[AUTO-ADVANCE] isLastQuestion=${isLastQuestion} (${currentQuestionIndex} >= ${questions.length - 1})`);
      
      // For last question, the completeInterview() call in submitAnswer handles it
      // Only auto-advance for non-last questions
      if (!isLastQuestion) {
        const autoAdvanceTimer = setTimeout(() => {
          console.log(`Auto-advancing to Q${currentQuestionIndex + 2}/${questions.length}`);
          
          // Stop speech recognition and ensure clean state before advancing
          if (speechRecognitionRef.current) {
            try {
              speechRecognitionRef.current.abort(); // Use abort() for immediate stop
            } catch (e) {}
          }
          
          // Reset transcript for next question
          transcriptRef.current = '';
          setCurrentTranscript('');
          setInterimTranscript('');
          setSpeechStatus('inactive');
          setIsRecording(false); // Ensure recording state is reset
          
          setCurrentQuestionIndex(prev => {
            const next = prev + 1;
            currentQuestionIndexRef.current = next;
            // Lock in the next question's ID
            if (questionsRef.current[next]) {
              activeQuestionIdRef.current = questionsRef.current[next].id;
            }
            return next;
          });
          setRecordedChunks([]);
          recordedChunksRef.current = [];
          // Directly start the next question - no waiting for user click
          setPhase('reading');
          const nextQuestion = questions[currentQuestionIndex + 1];
          setTimeLeft(nextQuestion?.listening_time || 30);
        }, 1500);
        return () => clearTimeout(autoAdvanceTimer);
      }
    }
  }, [phase, submitting, currentQuestionIndex, questions.length, interviewComplete, questions]);
  
  // Dedicated useEffect for TTS - triggers when question changes and phase is 'reading'
  // This ensures TTS plays the CURRENT question after state has fully updated
  useEffect(() => {
    if (phase === 'reading' && questions.length > 0 && currentQuestionIndex < questions.length) {
      const currentQ = questions[currentQuestionIndex];
      const questionText = currentQ?.question_text || currentQ?.text || '';
      
      console.log(`TTS Effect: Playing Q${currentQuestionIndex + 1}: "${questionText.substring(0, 50)}..."`);
      
      if (questionText && settings.enable_tts) {
        // Small delay to ensure UI has rendered the current question
        const ttsTimer = setTimeout(() => {
          speakQuestion(questionText);
        }, 100);
        return () => clearTimeout(ttsTimer);
      }
    }
  }, [phase, currentQuestionIndex, questions, settings.enable_tts]);

  // Proctoring detection loop
  useEffect(() => {
    if (modelsLoaded && cameraStream && videoRef.current && (phase === 'reading' || phase === 'answering')) {
      proctoringIntervalRef.current = setInterval(runProctoringChecks, 1000);
      return () => {
        if (proctoringIntervalRef.current) {
          clearInterval(proctoringIntervalRef.current);
        }
      };
    }
  }, [modelsLoaded, cameraStream, phase]);

  // Ensure video element is connected to stream
  useEffect(() => {
    if (cameraStream && videoRef.current && !videoRef.current.srcObject) {
      videoRef.current.srcObject = cameraStream;
      videoRef.current.play().catch(err => console.log('Video play error:', err));
    }
  }, [cameraStream]);

  const showWarning = (message, type = 'warning') => {
    setWarningMessage(message);
    setWarningType(type);
    setTimeout(() => setWarningMessage(''), 3000);
  };

  const logProctoringEvent = async (eventType, data = {}) => {
    try {
      await axios.post(`${API_URL}/api/interview/${token}/proctoring-event`, {
        event_type: eventType,
        data,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Failed to log proctoring event:', error);
    }
  };

  const runProctoringChecks = async () => {
    if (!videoRef.current || !modelsLoaded) return;

    try {
      // Use balanced thresholds - high enough to avoid false positives from background objects
      const detectorOptions = new faceapi.TinyFaceDetectorOptions({
        inputSize: 416,       // Standard input size
        scoreThreshold: 0.5   // Higher threshold to avoid detecting posters, shadows, artifacts as faces
      });
      
      const detections = await faceapi
        .detectAllFaces(videoRef.current, detectorOptions)
        .withFaceLandmarks()
        .withFaceDescriptors();

      // Multiple faces detection - with false positive filtering
      if (detections.length > 1) {
        // Find the primary face (largest detection box = closest to camera)
        const primaryFace = detections.reduce((a, b) => 
          a.detection.box.area > b.detection.box.area ? a : b
        );
        const primaryArea = primaryFace.detection.box.area;
        
        // Filter: only count secondary faces that are significant (at least 15% of primary face area)
        // and have high confidence (score > 0.55). This eliminates background posters, shadows, etc.
        const significantOtherFaces = detections.filter(d => {
          if (d === primaryFace) return false;
          const areaRatio = d.detection.box.area / primaryArea;
          const confidence = d.detection.score;
          return areaRatio > 0.15 && confidence > 0.55;
        });
        
        // Only flag if there are genuinely significant other faces
        // AND debounce: at least 10 seconds since last detection to avoid counting every frame
        const now = Date.now();
        const timeSinceLastDetection = now - (proctoring.lastMultipleFacesTime || 0);
        
        if (significantOtherFaces.length > 0 && timeSinceLastDetection > 10000) {
          setProctoring(prev => ({ 
            ...prev, 
            multipleFacesCount: prev.multipleFacesCount + 1,
            lastMultipleFacesTime: now
          }));
          showWarning(`Multiple faces detected (${significantOtherFaces.length + 1})! Only the interviewee should be visible.`, 'critical');
          logProctoringEvent('multiple_faces', { 
            count: significantOtherFaces.length + 1,
            faces: detections.map((d, i) => ({
              index: i,
              confidence: d.detection.score.toFixed(2),
              size: Math.round(d.detection.box.width * d.detection.box.height),
              isPrimary: d === primaryFace
            }))
          });
        
          // If we have a reference face, check which face matches
          if (referenceFaceDescriptorRef.current) {
            const distances = detections.map(d => 
              faceapi.euclideanDistance(referenceFaceDescriptorRef.current, d.descriptor)
            );
            const minDistance = Math.min(...distances);
            const matchingFaceIndex = distances.indexOf(minDistance);
            
            console.log('[Multi-Face Analysis] Face distances:', distances.map(d => d.toFixed(3)));
            console.log('[Multi-Face Analysis] Primary face index:', matchingFaceIndex, 'distance:', minDistance.toFixed(3));
            
            // Check if there's a face swap attempt (non-primary face is closer to camera)
            const matchedFace = detections[matchingFaceIndex];
            const otherFaces = detections.filter((_, i) => i !== matchingFaceIndex);
            
            otherFaces.forEach((face) => {
              if (face.detection.box.area > matchedFace.detection.box.area * 0.8) {
                showWarning('Unauthorized person detected in prominent position!', 'critical');
                logProctoringEvent('unauthorized_face_prominent', {
                  primaryFaceArea: matchedFace.detection.box.area,
                  otherFaceArea: face.detection.box.area
                });
              }
            });
          }
        }
      }

      if (detections.length === 1) {
        const detection = detections[0];
        const landmarks = detection.landmarks;
        const box = detection.detection.box;
        
        // IMPROVED Eye Contact Detection - Much stricter thresholds
        const leftEye = landmarks.getLeftEye();
        const rightEye = landmarks.getRightEye();
        const nose = landmarks.getNose();
        
        // Get video dimensions for reference
        const videoWidth = videoRef.current.videoWidth || 640;
        const videoHeight = videoRef.current.videoHeight || 480;
        
        // Calculate face center in normalized coordinates (0-1)
        const faceCenterX = (box.x + box.width / 2) / videoWidth;
        const faceCenterY = (box.y + box.height / 2) / videoHeight;
        
        // Calculate eye positions
        const leftEyeCenter = {
          x: leftEye.reduce((sum, p) => sum + p.x, 0) / leftEye.length,
          y: leftEye.reduce((sum, p) => sum + p.y, 0) / leftEye.length
        };
        const rightEyeCenter = {
          x: rightEye.reduce((sum, p) => sum + p.x, 0) / rightEye.length,
          y: rightEye.reduce((sum, p) => sum + p.y, 0) / rightEye.length
        };
        
        // Eye distance - used to normalize measurements
        const eyeDistance = Math.sqrt(
          Math.pow(rightEyeCenter.x - leftEyeCenter.x, 2) + 
          Math.pow(rightEyeCenter.y - leftEyeCenter.y, 2)
        );
        
        // Calculate head rotation based on eye line angle (tilt/roll)
        const eyeAngle = Math.atan2(
          rightEyeCenter.y - leftEyeCenter.y,
          rightEyeCenter.x - leftEyeCenter.x
        ) * 180 / Math.PI;
        
        // Calculate yaw (left/right head turn) using nose position
        const noseTip = nose[3]; // Tip of nose
        const eyesMidX = (leftEyeCenter.x + rightEyeCenter.x) / 2;
        const eyesMidY = (leftEyeCenter.y + rightEyeCenter.y) / 2;
        
        // Nose offset from center of eyes (indicates head turn left/right)
        // Normalized by eye distance for scale-invariance
        const noseOffsetX = (noseTip.x - eyesMidX) / eyeDistance;
        
        // IMPROVED PITCH DETECTION (looking up/down)
        // When looking down: face tilts forward, forehead becomes more visible, 
        // face center moves up in frame, and nose-to-eye vertical distance changes
        
        // Method 1: Face position in frame - when looking down, face shifts UP
        const faceTopRatio = box.y / videoHeight; // How far from top (0 = top, 1 = bottom)
        
        // Method 2: Eye position relative to face box
        // When looking down, eyes appear higher in the face bounding box
        const eyePositionInFace = (eyesMidY - box.y) / box.height;
        
        // Method 3: Nose-to-eye vertical ratio (more reliable for down-looking)
        const noseToEyeVertical = (noseTip.y - eyesMidY) / box.height;
        
        // Calculate eye aspect ratios to detect if eyes are actually open and looking
        const leftEAR = getEyeAspectRatio(leftEye);
        const rightEAR = getEyeAspectRatio(rightEye);
        const avgEAR = (leftEAR + rightEAR) / 2;
        
        // LENIENT CRITERIA for eye contact (accounts for different camera positions):
        
        // 1. Face must be reasonably in frame (very lenient - cameras vary in position)
        const faceInCenter = faceCenterX > 0.15 && faceCenterX < 0.85 && 
                            faceCenterY > 0.10 && faceCenterY < 0.90;
        
        // 2. Head tilt (roll) - allow natural tilts (within ±15 degrees - more lenient)
        const headTiltOK = Math.abs(eyeAngle) < 15;
        
        // 3. Head turn left/right - more lenient for laptop cameras on sides
        const headNotTurnedLR = Math.abs(noseOffsetX) < 0.25;
        
        // 4. HEAD PITCH CHECK - More lenient for cameras at different heights
        // Laptops often have cameras at different positions (top of screen, bottom, external webcams)
        // Normal range expanded: eyePositionInFace ~0.22-0.45, noseToEyeVertical ~0.20-0.42
        // Only flag extreme looking down (reading from paper) or looking up (at ceiling)
        const isLookingDown = eyePositionInFace > 0.45 || noseToEyeVertical > 0.42 || faceTopRatio < 0.08;
        const isLookingUp = eyePositionInFace < 0.18;
        const headNotTurnedUD = !isLookingDown && !isLookingUp;
        
        // 5. Face size - very lenient range for different camera distances
        const faceSizeOK = box.width > videoWidth * 0.10 && box.width < videoWidth * 0.80;
        
        // 6. Eyes open check - slightly more lenient
        const eyesOpen = avgEAR > 0.15;
        
        // 7. Face position - very lenient (cameras can be above, below, or at eye level)
        const faceNotTooHigh = faceCenterY > 0.15;
        
        // Debug logging (always log to help diagnose issues)
        console.log('[Eye Contact Detection]', {
            eyePos: eyePositionInFace.toFixed(3),
            noseEyeV: noseToEyeVertical.toFixed(3),
            faceTop: faceTopRatio.toFixed(3),
            noseOffX: noseOffsetX.toFixed(3),
            angle: eyeAngle.toFixed(1),
            pitch: isLookingDown ? '⬇️ DOWN' : isLookingUp ? '⬆️ UP' : '✓ OK',
            checks: {
              center: faceInCenter ? '✓' : '✗',
              tilt: headTiltOK ? '✓' : '✗',
              turn: headNotTurnedLR ? '✓' : '✗',
              pitch: headNotTurnedUD ? '✓' : '✗',
              size: faceSizeOK ? '✓' : '✗',
              eyes: eyesOpen ? '✓' : '✗',
              pos: faceNotTooHigh ? '✓' : '✗'
            },
            result: faceInCenter && headTiltOK && headNotTurnedLR && headNotTurnedUD && faceSizeOK && eyesOpen && faceNotTooHigh ? '✅ CONTACT' : '❌ NO CONTACT'
          });
        
        // ALL criteria must pass for positive eye contact
        const isLookingAtCamera = faceInCenter && headTiltOK && headNotTurnedLR && 
                                   headNotTurnedUD && faceSizeOK && eyesOpen && faceNotTooHigh;
        
        // Update BOTH cumulative and per-question eye contact
        setProctoring(prev => {
          // Cumulative tracking
          const newSamples = prev.eyeContactSamples + 1;
          const newPositive = prev.eyeContactPositive + (isLookingAtCamera ? 1 : 0);
          const rawPercent = Math.round((newPositive / newSamples) * 100);
          
          // Per-question tracking
          const qSamples = prev.currentQuestionEyeContact.samples + 1;
          const qPositive = prev.currentQuestionEyeContact.positive + (isLookingAtCamera ? 1 : 0);
          const qPercent = Math.round((qPositive / qSamples) * 100);
          
          return {
            ...prev,
            eyeContactSamples: newSamples,
            eyeContactPositive: newPositive,
            eyeContactPercent: rawPercent,
            consecutiveFaceMisses: 0, // Reset consecutive misses when face is detected
            currentQuestionEyeContact: {
              samples: qSamples,
              positive: qPositive,
              percent: qPercent
            }
          };
        });

        // Face match with reference (if available)
        if (referenceFaceDescriptorRef.current && detection.descriptor) {
          const distance = faceapi.euclideanDistance(
            referenceFaceDescriptorRef.current,
            detection.descriptor
          );
          
          // Improved distance-to-match calculation:
          // face-api.js euclidean distance typically ranges 0.0 to 1.2+
          // Same person: usually < 0.4
          // Different person: usually > 0.5
          // We use a threshold-based calculation for better accuracy
          let matchPercent;
          if (distance < 0.35) {
            matchPercent = 95 + (0.35 - distance) * 14; // 95-100% for very similar
          } else if (distance < 0.45) {
            matchPercent = 80 + (0.45 - distance) * 150; // 80-95% for similar
          } else if (distance < 0.55) {
            matchPercent = 60 + (0.55 - distance) * 200; // 60-80% for moderate
          } else if (distance < 0.70) {
            matchPercent = 30 + (0.70 - distance) * 200; // 30-60% for questionable
          } else {
            matchPercent = Math.max(0, 30 - (distance - 0.70) * 50); // 0-30% for different person
          }
          matchPercent = Math.round(Math.min(100, Math.max(0, matchPercent)));
          
          console.log('[Face Match]', {
            distance: distance.toFixed(3),
            matchPercent,
            verdict: distance < 0.5 ? '✅ MATCH' : distance < 0.6 ? '⚠️ QUESTIONABLE' : '❌ MISMATCH'
          });
          
          setProctoring(prev => {
            const newSamples = prev.faceMatchSamples + 1;
            const runningAvg = ((prev.faceMatchPercent * (newSamples - 1)) + matchPercent) / newSamples;
            
            // Robust face mismatch detection using consecutive mismatch window
            // Only record a real face_mismatch event after N consecutive high-distance frames
            let consecutiveMismatches = prev.consecutiveMismatches || 0;
            let faceSwapEvents = prev.faceSwapEvents || 0;
            let lastMismatchEventTime = prev.lastMismatchEventTime || 0;
            
            if (distance > 0.55) {
              consecutiveMismatches++;
            } else {
              // Reset counter on a good match
              consecutiveMismatches = 0;
            }
            
            // Only log a face_mismatch event if:
            // 1. At least 5 consecutive frames show mismatch (not a flicker)
            // 2. At least 30 seconds since last mismatch event (prevent flooding)
            const now = Date.now();
            if (consecutiveMismatches >= 5 && (now - lastMismatchEventTime) > 30000) {
              faceSwapEvents++;
              lastMismatchEventTime = now;
              
              logProctoringEvent('face_mismatch', { 
                matchPercent, 
                distance: distance.toFixed(3),
                severity: distance > 0.7 ? 'critical' : 'warning',
                consecutiveFrames: consecutiveMismatches
              });
              
              if (distance > 0.7) {
                showWarning('Face verification alert: Face does not match ID photo.', 'critical');
              } else {
                showWarning('Face verification warning: Poor match with ID photo.', 'warning');
              }
              
              // Reset counter after logging
              consecutiveMismatches = 0;
            }
            
            return {
              ...prev,
              faceMatchSamples: newSamples,
              faceMatchPercent: Math.round(runningAvg),
              lastFaceDistance: distance,
              faceSwapEvents,
              consecutiveMismatches,
              lastMismatchEventTime
            };
          });
        } else if (!referenceFaceDescriptorRef.current) {
          // No reference face - this is expected for older applications
          // Don't log every time, just once
          if (proctoring.faceMatchSamples === 0) {
            console.log('[Face Match] No reference descriptor available - face matching disabled');
          }
        }

        // Blink detection for liveness - reuse EAR calculated above
        if (avgEAR < 0.2) { // Eye closed threshold
          const now = Date.now();
          if (now - proctoring.lastBlinkTime > 500) {
            setProctoring(prev => ({
              ...prev,
              blinkCount: prev.blinkCount + 1,
              lastBlinkTime: now,
              livenessScore: Math.min(100, prev.livenessScore + 2)
            }));
          }
        }
      } else if (detections.length === 0) {
        // No face detected - but could be momentary (lighting, movement, etc.)
        // Only count as "absent" after multiple consecutive misses
        console.log('[Proctoring] No face detected in this frame');
        
        setProctoring(prev => {
          const consecutiveMisses = (prev.consecutiveFaceMisses || 0) + 1;
          
          // Only count as truly absent after 3+ consecutive misses (3 seconds)
          if (consecutiveMisses >= 3) {
            return {
              ...prev,
              consecutiveFaceMisses: consecutiveMisses,
              faceAbsentCount: prev.faceAbsentCount + 1,
              faceAbsentDuration: prev.faceAbsentDuration + 1,
              eyeContactPercent: Math.max(0, prev.eyeContactPercent - 5),
              livenessScore: Math.max(0, prev.livenessScore - 3),
            };
          }
          
          return {
            ...prev,
            consecutiveFaceMisses: consecutiveMisses
          };
        });
        
        // Only show warning after sustained absence
        if ((proctoring.consecutiveFaceMisses || 0) >= 3) {
          showWarning('Face not detected! Please stay in front of the camera.');
        }
      }
    } catch (error) {
      console.error('Proctoring check error:', error);
    }
  };

  const getEyeAspectRatio = (eye) => {
    const vertical1 = Math.sqrt(Math.pow(eye[1].x - eye[5].x, 2) + Math.pow(eye[1].y - eye[5].y, 2));
    const vertical2 = Math.sqrt(Math.pow(eye[2].x - eye[4].x, 2) + Math.pow(eye[2].y - eye[4].y, 2));
    const horizontal = Math.sqrt(Math.pow(eye[0].x - eye[3].x, 2) + Math.pow(eye[0].y - eye[3].y, 2));
    return (vertical1 + vertical2) / (2 * horizontal);
  };

  // AWS Rekognition Face Swap Detection - Real-time comparison
  const checkFaceSwapAWS = async () => {
    // Only run if we have a reference face image and video is available
    if (!referenceFaceImageRef.current || !videoRef.current || !cameraStream) {
      return;
    }
    
    try {
      // Capture current frame from video
      const canvas = document.createElement('canvas');
      const video = videoRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const currentFrameBase64 = canvas.toDataURL('image/jpeg', 0.8);
      
      // Call AWS Rekognition endpoint
      const response = await axios.post(`${API_URL}/api/interview/${token}/detect-face-swap`, {
        reference_image: referenceFaceImageRef.current,
        current_image: currentFrameBase64,
        similarity_threshold: 70.0
      });
      
      if (response.data.success) {
        const { is_same_person, face_swap_detected, similarity_score, message } = response.data;
        
        setProctoring(prev => ({
          ...prev,
          awsFaceSwapLastCheck: new Date().toISOString(),
          awsFaceSwapEvents: face_swap_detected ? prev.awsFaceSwapEvents + 1 : prev.awsFaceSwapEvents
        }));
        
        // Log the event
        if (face_swap_detected) {
          logProctoringEvent('aws_face_swap_detected', {
            similarity_score: similarity_score.toFixed(1),
            message
          });
          
          // Show warning for face swap
          showWarning(`🚨 FACE SWAP DETECTED: ${message}`, 'critical');
          console.warn(`[AWS Face Swap] Detection triggered: ${message} (similarity: ${similarity_score}%)`);
        } else {
          console.log(`[AWS Face Swap] Verified - Same person (similarity: ${similarity_score?.toFixed(1) || 'N/A'}%)`);
        }
      }
    } catch (error) {
      // Don't block interview on AWS errors - just log
      console.warn('[AWS Face Swap] Check failed:', error.message);
    }
  };

  // Start AWS face swap detection when interview begins
  useEffect(() => {
    // Start AWS face swap detection when moving from mic-test to countdown
    if (phase === 'countdown' || phase === 'reading' || phase === 'answering') {
      // Check every 20 seconds (to balance accuracy vs API costs)
      if (!awsFaceSwapIntervalRef.current && referenceFaceImageRef.current) {
        console.log('[AWS Face Swap] Starting real-time detection (every 20s)');
        
        // Initial check
        setTimeout(() => checkFaceSwapAWS(), 5000);
        
        // Periodic checks
        awsFaceSwapIntervalRef.current = setInterval(() => {
          checkFaceSwapAWS();
        }, 20000); // Every 20 seconds
      }
    }
    
    // Clean up on interview complete
    if (phase === 'complete' || phase === 'mic-test') {
      if (awsFaceSwapIntervalRef.current) {
        clearInterval(awsFaceSwapIntervalRef.current);
        awsFaceSwapIntervalRef.current = null;
      }
    }
    
    return () => {
      if (awsFaceSwapIntervalRef.current) {
        clearInterval(awsFaceSwapIntervalRef.current);
        awsFaceSwapIntervalRef.current = null;
      }
    };
  }, [phase]);

  // Audio analysis for voice detection with multiple voice detection
  const setupAudioAnalysis = (stream) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      analyser.fftSize = 512; // Increased for better frequency resolution
      
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      
      // Baseline voice frequency tracking
      let baselineFrequencies = null;
      let voiceCharacteristics = { avgPitch: 0, samples: 0 };
      
      // Monitor audio levels
      const checkAudio = () => {
        if (!analyserRef.current) return;
        
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);
        
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        
        // Voice frequency analysis (human voice is typically 85-255 Hz for fundamentals)
        // With harmonics extending to ~3400 Hz
        const voiceRangeLow = Math.floor(85 * dataArray.length / (audioContext.sampleRate / 2));
        const voiceRangeHigh = Math.floor(3400 * dataArray.length / (audioContext.sampleRate / 2));
        const voiceData = dataArray.slice(voiceRangeLow, voiceRangeHigh);
        
        // Calculate dominant frequency (pitch indicator)
        let maxVal = 0;
        let dominantIndex = 0;
        voiceData.forEach((val, idx) => {
          if (val > maxVal) {
            maxVal = val;
            dominantIndex = idx;
          }
        });
        
        const dominantFreq = (voiceRangeLow + dominantIndex) * (audioContext.sampleRate / 2) / dataArray.length;
        
        setProctoring(prev => {
          const history = [...prev.audioLevelHistory, average].slice(-100);
          
          let multipleVoicesDetected = false;
          let naturalScore = prev.naturalToneScore;
          
          // Detect multiple voices through spectral analysis
          if (history.length > 10 && average > 10) { // Only analyze when there's actual audio
            const recentAvg = history.slice(-10).reduce((a, b) => a + b) / 10;
            const olderAvg = history.slice(-20, -10).reduce((a, b) => a + b) / 10;
            
            // Natural tone analysis - check for robotic/reading patterns
            const variance = history.reduce((sum, val) => sum + Math.pow(val - recentAvg, 2), 0) / history.length;
            naturalScore = Math.min(100, Math.max(0, 50 + variance * 2));
            
            // Update baseline voice characteristics
            if (voiceCharacteristics.samples < 50 && average > 15) {
              voiceCharacteristics.avgPitch = 
                (voiceCharacteristics.avgPitch * voiceCharacteristics.samples + dominantFreq) / 
                (voiceCharacteristics.samples + 1);
              voiceCharacteristics.samples++;
            }
            
            // Multiple voice detection: Check for significant pitch shifts
            // A different person would have a noticeably different fundamental frequency
            if (voiceCharacteristics.samples >= 20 && average > 15) {
              const pitchDeviation = Math.abs(dominantFreq - voiceCharacteristics.avgPitch);
              const relativeDeviation = pitchDeviation / voiceCharacteristics.avgPitch;
              
              // If pitch deviates more than 40% from baseline, likely a different voice
              if (relativeDeviation > 0.4 && maxVal > 50) {
                multipleVoicesDetected = true;
                console.log('[Voice Analysis] Possible different voice detected:', {
                  baseline: voiceCharacteristics.avgPitch.toFixed(0),
                  current: dominantFreq.toFixed(0),
                  deviation: (relativeDeviation * 100).toFixed(1) + '%'
                });
              }
            }
            
            // Check for overlapping frequencies (two people talking)
            // Look for multiple peaks in voice frequency range
            let peaks = 0;
            let lastWasLow = true;
            voiceData.forEach((val, idx) => {
              if (val > 40 && lastWasLow) {
                peaks++;
                lastWasLow = false;
              } else if (val < 20) {
                lastWasLow = true;
              }
            });
            
            if (peaks >= 3 && average > 20) {
              multipleVoicesDetected = true;
              console.log('[Voice Analysis] Multiple frequency peaks detected - possible multiple voices');
            }
          }
          
          // Update multiple voices count if detected
          let multipleVoicesCount = prev.multipleVoicesCount || 0;
          if (multipleVoicesDetected) {
            multipleVoicesCount++;
            if (multipleVoicesCount % 3 === 1) { // Alert every 3rd detection to avoid spam
              showWarning('⚠️ Multiple voices or different speaker detected!', 'warning');
              logProctoringEvent('multiple_voices', { 
                dominantFreq: dominantFreq.toFixed(0),
                baseline: voiceCharacteristics.avgPitch.toFixed(0)
              });
            }
          }
          
          return {
            ...prev,
            audioLevelHistory: history,
            voiceActivityPercent: Math.round(average),
            naturalToneScore: Math.round((prev.naturalToneScore + naturalScore) / 2),
            multipleVoicesCount,
            dominantFrequency: dominantFreq
          };
        });
        
        if (phase === 'answering') {
          requestAnimationFrame(checkAudio);
        }
      };
      
      checkAudio();
    } catch (error) {
      console.error('Audio analysis setup failed:', error);
    }
  };

  useEffect(() => {
    fetchQuestions();
    return () => {
      stopFullRecording();
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
      if (speechSynthRef.current) {
        window.speechSynthesis.cancel();
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (proctoringIntervalRef.current) {
        clearInterval(proctoringIntervalRef.current);
      }
    };
  }, [token]);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/interview/${token}/questions`);
      const fetchedQuestions = response.data.questions || [];
      
      console.log(`[Interview] Loaded ${fetchedQuestions.length} questions (max setting: ${response.data.max_questions_setting}, total from API: ${response.data.total_questions})`);
      
      if (fetchedQuestions.length === 0) {
        toast.error('No interview questions available. Please contact support.');
        navigate('/');
        return;
      }
      
      if (fetchedQuestions.length < 2) {
        console.warn(`[Interview] WARNING: Only ${fetchedQuestions.length} question(s) loaded! Expected 10+`);
      }
      
      setQuestions(fetchedQuestions);
      questionsRef.current = fetchedQuestions;
      setUniversityName(response.data.university_name || '');
      if (response.data.settings) {
        setSettings(response.data.settings);
        // Set total interview time from admin settings (in minutes → seconds)
        const totalMinutes = response.data.settings.total_interview_time || 10;
        setTotalTimeLeft(totalMinutes * 60);
      } else {
        setTotalTimeLeft(600); // Default 10 minutes
      }
      // Try to get reference face descriptor for face-api.js matching
      if (response.data.reference_face_descriptor) {
        referenceFaceDescriptorRef.current = new Float32Array(response.data.reference_face_descriptor);
      }
      // Try to get reference face image for AWS face swap detection
      if (response.data.reference_face_image) {
        referenceFaceImageRef.current = response.data.reference_face_image;
        console.log('[AWS Face Swap] Reference face image loaded for real-time detection');
      }
      await startCamera();
    } catch (error) {
      console.error('Failed to load interview questions:', error);
      toast.error(error.response?.data?.detail || 'Failed to load interview questions');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const startCamera = async () => {
    try {
      setCameraError(false);
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' }, 
        audio: true 
      });
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setupAudioAnalysis(stream);
      return stream;
    } catch (error) {
      console.error('Camera error:', error);
      setCameraError(true);
      toast.error('Failed to access camera. Please allow camera permissions and refresh.');
      return null;
    }
  };

  const speakQuestion = async (text) => {
    if (!text) return;
    
    // Cancel any ongoing browser TTS
    window.speechSynthesis.cancel();
    setIsSpeaking(true);
    
    try {
      // Use ElevenLabs TTS via backend
      const response = await axios.post(`${API_URL}/api/tts/generate`, {
        text: text
      });
      
      if (response.data.success && response.data.audio_url) {
        // Create audio element and play
        const audio = new Audio(response.data.audio_url);
        audio.onended = () => setIsSpeaking(false);
        audio.onerror = () => {
          console.error('Audio playback error');
          setIsSpeaking(false);
        };
        await audio.play();
      } else {
        throw new Error('No audio data received');
      }
    } catch (error) {
      console.error('ElevenLabs TTS error:', error);
      // Fallback to browser TTS if ElevenLabs fails
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.9;
      utterance.pitch = 1.1;
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);
      window.speechSynthesis.speak(utterance);
    }
  };

  const startFullRecording = () => {
    if (!cameraStream || isFullRecording) return;
    
    try {
      fullChunksRef.current = [];
      // Use VP9 with constrained bitrate for better compression
      const mediaRecorder = new MediaRecorder(cameraStream, {
        mimeType: 'video/webm;codecs=vp9,opus',
        videoBitsPerSecond: 1000000, // 1 Mbps for good quality with smaller file size
        audioBitsPerSecond: 64000    // 64 kbps for audio
      });
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          fullChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.start(1000);
      fullRecorderRef.current = mediaRecorder;
      setIsFullRecording(true);
    } catch (error) {
      console.error('Failed to start full recording:', error);
    }
  };

  const stopFullRecording = () => {
    if (fullRecorderRef.current && fullRecorderRef.current.state !== 'inactive') {
      fullRecorderRef.current.stop();
      setIsFullRecording(false);
    }
  };

  const uploadFullRecording = async () => {
    if (fullChunksRef.current.length === 0) return;
    
    setUploadingRecording(true);
    try {
      const blob = new Blob(fullChunksRef.current, { type: 'video/webm' });
      
      // Convert blob to base64
      const reader = new FileReader();
      const base64Data = await new Promise((resolve, reject) => {
        reader.onloadend = () => {
          // Remove the data URL prefix (data:video/webm;base64,)
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      
      // Upload to Google Drive via backend
      const response = await axios.post(`${API_URL}/api/interview/${token}/upload-video`, {
        video_data: base64Data,
        filename: `interview_full_${Date.now()}.webm`,
        interview_id: token,
        recording_type: 'full'
      });
      
      if (response.data.success) {
        toast.success('Interview recording uploaded to Google Drive!');
        console.log('Recording uploaded:', response.data.embed_url);
      } else {
        console.warn('Recording upload warning:', response.data.message);
      }
    } catch (error) {
      console.error('Failed to upload recording:', error);
    } finally {
      setUploadingRecording(false);
    }
  };

  const startQuestion = useCallback(() => {
    if (questions.length === 0 || currentQuestionIndex >= questions.length) return;
    
    const question = questions[currentQuestionIndex];
    const questionText = question.question_text || question.text || '';
    
    // Lock in the question ID for this question period
    activeQuestionIdRef.current = question.id;
    
    console.log(`Starting Question ${currentQuestionIndex + 1}/${questions.length}: [${question.id}] ${questionText.substring(0, 50)}`);
    
    // Set phase to 'reading' - this will trigger the TTS useEffect
    // The useEffect ensures TTS plays after state is fully updated
    setPhase('reading');
    setTimeLeft(question.listening_time || 30);
    
    // TTS is now handled by the dedicated useEffect hook
    // This prevents stale closure issues with currentQuestionIndex
    
    if (currentQuestionIndex === 0 && !isFullRecording) {
      startFullRecording();
    }
  }, [questions, currentQuestionIndex, isFullRecording]);

  // Handle the 'starting' phase transition (countdown finished → start first question)
  useEffect(() => {
    if (phase === 'starting') {
      console.log(`[PHASE] starting → calling startQuestion(). questions.length=${questions.length}, idx=${currentQuestionIndex}`);
      startQuestion();
    }
  }, [phase, startQuestion]);

  // Debug: log all phase transitions
  useEffect(() => {
    console.log(`[PHASE CHANGE] phase=${phase}, idx=${currentQuestionIndex}, qLen=${questions.length}, submitting=${submitting}, complete=${interviewComplete}`);
  }, [phase, currentQuestionIndex, questions.length, submitting, interviewComplete]);

  const startAnswering = () => {
    const currentQuestion = questions[currentQuestionIndex];
    setPhase('answering');
    setTimeLeft(currentQuestion.answering_time);
    setIsSpeaking(false);
    window.speechSynthesis.cancel();
    
    // Reset per-question eye contact tracking at start of each question
    setProctoring(prev => ({
      ...prev,
      currentQuestionEyeContact: { samples: 0, positive: 0, percent: 100 }
    }));
    
    startRecording();
  };

  const startRecording = () => {
    if (!cameraStream) {
      console.warn('Cannot start recording - no camera stream available');
      toast.error('Camera not available. Video will not be recorded for this answer.');
      return;
    }
    
    // Reset transcript for new question
    transcriptRef.current = '';
    setCurrentTranscript('');
    setInterimTranscript('');
    setSpeechStatus('inactive');
    
    try {
      // Try VP9 first, fall back to VP8, then default
      let mimeType = 'video/webm;codecs=vp9,opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'video/webm;codecs=vp8,opus';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'video/webm';
        }
      }
      console.log(`Starting recording with mimeType: ${mimeType}`);
      
      const mediaRecorder = new MediaRecorder(cameraStream, {
        mimeType,
        videoBitsPerSecond: 1000000,
        audioBitsPerSecond: 64000
      });
      
      mediaRecorderRef.current = mediaRecorder;
      setRecordedChunks([]);
      recordedChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
          setRecordedChunks(prev => [...prev, event.data]);
        }
      };
      
      mediaRecorder.onstop = () => {
        console.log(`MediaRecorder stopped for Q${currentQuestionIndex + 1}. Chunks collected: ${recordedChunksRef.current.length}`);
      };
      
      mediaRecorder.start(1000);
      setIsRecording(true);
      
      // Start speech recognition - use abort() first to ensure completely clean state
      if (speechRecognitionRef.current) {
        try {
          speechRecognitionRef.current.abort();
        } catch (e) {}
        
        // Give browser time to fully release resources, then start fresh
        setTimeout(() => {
          try {
            speechRecognitionRef.current.start();
            console.log('Speech recognition started for Q' + (currentQuestionIndex + 1));
          } catch (e) {
            console.log('Speech recognition start error (attempt 1):', e.message);
            // Try again after a longer delay
            setTimeout(() => {
              try {
                speechRecognitionRef.current.start();
                console.log('Speech recognition started on retry');
              } catch (e2) {
                console.error('Failed to start speech recognition:', e2.message);
                toast.error('Could not start transcription. Try refreshing the page.');
              }
            }, 500);
          }
        }, 300);
      }
    } catch (error) {
      console.error('Recording error:', error);
      setIsRecording(true);
    }
  };

  const stopRecording = async () => {
    // IMPORTANT: Stop speech recognition FIRST and wait for final results
    if (speechRecognitionRef.current) {
      try {
        // Set recording flag to false BEFORE stopping to prevent auto-restart
        isRecordingRef.current = false;
        speechRecognitionRef.current.stop();
        console.log('Speech recognition stopped, current transcript length:', transcriptRef.current.length);
      } catch (e) {
        console.log('Stop speech recognition error:', e);
      }
    }
    
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.stop();
      } catch (e) {
        console.error('Stop recording error:', e);
      }
    }
    setIsRecording(false);
    
    // Wait a bit longer to allow any pending speech results to be processed
    // This is critical to capture the final words of the answer
    setTimeout(() => {
      console.log('Final transcript before submit:', transcriptRef.current.substring(0, 100) + '...');
      submitAnswer();
    }, 800); // Increased from 500ms to 800ms to allow final speech results
  };

  const submitAnswer = async () => {
    setSubmitting(true);
    setPhase('submitted');
    
    // Use refs for stable values (avoid stale closures from setTimeout)
    const qLen = questionsRef.current.length;
    const qIdx = currentQuestionIndexRef.current;
    const currentQuestion = questionsRef.current[qIdx];
    
    // Use the locked-in question ID (set when question started reading)
    const lockedQuestionId = activeQuestionIdRef.current;
    
    // CRITICAL DEBUG
    console.log(`=== SUBMIT ANSWER ===`);
    console.log(`qIdx=${qIdx}, qLen=${qLen}, lockedQId=${lockedQuestionId}, refQId=${currentQuestion?.id}`);
    
    if (!currentQuestion && !lockedQuestionId) {
      console.error(`No question at index ${qIdx}! questions ref length: ${qLen}`);
      setSubmitting(false);
      return;
    }
    
    // Use locked question ID as primary, ref as fallback
    const questionIdToSubmit = lockedQuestionId || currentQuestion?.id;
    
    // Get transcript
    let answerTranscript = transcriptRef.current.trim();
    if (!answerTranscript && interimTranscript) {
      answerTranscript = interimTranscript.trim();
    }
    answerTranscript = answerTranscript || 'No speech detected';
    
    const isLastQuestion = qIdx >= qLen - 1;
    console.log(`Submitting Q${qIdx + 1}/${qLen}. isLastQuestion=${isLastQuestion}, questionId=${questionIdToSubmit}`);
    
    // Get the per-question eye contact percentage
    const questionEyeContact = proctoring.currentQuestionEyeContact.percent;
    
    console.log(`Submitting Q${qIdx + 1}/${qLen}. Eye Contact: ${questionEyeContact}%`);
    
    // Save this question's eye contact to the array
    setProctoring(prev => ({
      ...prev,
      questionsEyeContact: [
        ...prev.questionsEyeContact,
        { questionIndex: qIdx, percent: questionEyeContact }
      ]
    }));
    
    try {
      // First, submit the answer (this creates the questions_answers entry)
      await axios.post(`${API_URL}/api/interview/${token}/submit-answer`, {
        question_id: questionIdToSubmit,
        video_url: null, // Will be updated after upload
        transcript: answerTranscript,
        duration: (currentQuestion?.answering_time || 90) - timeLeft,
        proctoring_snapshot: {
          // Per-question eye contact (THIS question only)
          questionEyeContactPercent: questionEyeContact,
          // Cumulative metrics
          cumulativeEyeContactPercent: proctoring.eyeContactPercent,
          tabSwitchCount: proctoring.tabSwitchCount,
          multipleFacesCount: proctoring.multipleFacesCount,
          faceMatchPercent: proctoring.faceMatchPercent,
          livenessScore: proctoring.livenessScore,
          faceAbsentCount: proctoring.faceAbsentCount,
          faceAbsentDuration: proctoring.faceAbsentDuration
        }
      });
      
      // Now upload video — WAIT for upload to complete before marking as done
      const currentChunks = [...recordedChunksRef.current];
      if (currentChunks.length > 0) {
        const blob = new Blob(currentChunks, { type: 'video/webm' });
        console.log(`Video blob for Q${qIdx + 1}: ${currentChunks.length} chunks, ${blob.size} bytes`);
        
        // Convert to base64 and upload — with retry
        try {
          const base64Data = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          
          // Attempt upload with retry
          let uploadSuccess = false;
          for (let attempt = 1; attempt <= 3; attempt++) {
            try {
              await axios.post(`${API_URL}/api/interview/${token}/upload-video`, {
                video_data: base64Data,
                filename: `answer_q${qIdx + 1}_${Date.now()}.webm`,
                interview_id: token,
                question_id: questionIdToSubmit,
                question_index: qIdx,
                recording_type: 'answer'
              });
              console.log(`Answer video uploaded for Q${qIdx + 1} (attempt ${attempt})`);
              uploadSuccess = true;
              break;
            } catch (uploadErr) {
              console.warn(`Video upload attempt ${attempt} failed for Q${qIdx + 1}:`, uploadErr.message);
              if (attempt < 3) {
                await new Promise(r => setTimeout(r, 2000)); // Wait 2s before retry
              }
            }
          }
          
          if (!uploadSuccess) {
            console.error(`FAILED to upload video for Q${qIdx + 1} after 3 attempts`);
            toast.error(`Video for Q${currentQuestionIndex + 1} could not be saved. Please ensure stable internet.`);
          }
        } catch (readErr) {
          console.error('Failed to read video blob:', readErr);
        }
      } else {
        console.warn(`No video chunks recorded for Q${qIdx + 1}!`);
      }
      
      // Handle last question - complete interview immediately
      if (isLastQuestion) {
        console.log('Last question submitted - completing interview...');
        stopFullRecording();
        await completeInterview();
      }
    } catch (error) {
      console.error('Failed to submit answer:', error);
      toast.error('Failed to submit answer');
      
      // Even on error, if it's the last question, complete the interview
      if (isLastQuestion) {
        console.log('Last question - completing interview despite error...');
        stopFullRecording();
        await completeInterview();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const completeInterview = async () => {
    console.log('=== COMPLETING INTERVIEW ===');
    console.log('Proctoring data:', JSON.stringify(proctoring, null, 2));
    
    try {
      const response = await axios.post(`${API_URL}/api/interview/${token}/complete`, {
        proctoring_summary: {
          ...proctoring,
          fullscreen_exit_count: fullscreenExitCount
        }
      });
      console.log('Complete interview response:', response.data);
      setResults(response.data);
      toast.success('Interview completed!');
    } catch (error) {
      console.error('Complete interview error:', error);
      console.error('Error response:', error.response?.data);
      
      // Show more specific error message
      if (error.response?.status === 404) {
        toast.error('Interview not found. It may have already been completed.');
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.detail || 'Interview already completed.');
      } else {
        toast.error('Failed to complete interview. Please contact support.');
      }
      
      // Set default results even on error
      setResults({ message: 'Interview completed' });
    } finally {
      // Always mark as complete so the feedback screen shows
      setInterviewComplete(true);
      // Exit fullscreen when interview is done
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
      // Try to upload recording in background
      uploadFullRecording().catch(e => console.error('Upload error:', e));
    }
  };

  const submitFeedback = async () => {
    if (feedbackRating === 0) {
      toast.error('Please select a star rating');
      return;
    }
    
    setSubmittingFeedback(true);
    try {
      await axios.post(`${API_URL}/api/interview/${token}/feedback`, {
        rating: feedbackRating,
        feedback_text: feedbackText.trim(),
        submitted_at: new Date().toISOString()
      });
      setFeedbackSubmitted(true);
      toast.success('Thank you for your feedback!');
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      toast.error('Failed to submit feedback');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-lime-500 mx-auto mb-4" />
          <p className="text-slate-400">Preparing your interview...</p>
        </div>
      </div>
    );
  }

  if (interviewComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f] p-4">
        <Card className="max-w-lg w-full bg-[#12121a] border-slate-800">
          <CardContent className="p-8">
            {feedbackSubmitted ? (
              // Thank You Screen after feedback submission
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-lime-500/20 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-10 h-10 text-lime-500" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-3">Thank You!</h2>
                <p className="text-slate-400 mb-6">
                  Your interview has been submitted successfully. We appreciate your feedback and will review your responses shortly.
                </p>
                <p className="text-slate-500 text-sm mb-8">
                  You will be notified of the results via email.
                </p>
                <Button 
                  onClick={() => navigate('/')} 
                  className="w-full bg-lime-500 hover:bg-lime-600 text-black font-medium"
                  data-testid="return-home-btn"
                >
                  Return to Home
                </Button>
              </div>
            ) : (
              // Feedback Form
              <div>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 rounded-full bg-lime-500/20 flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="w-8 h-8 text-lime-500" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">Interview Complete!</h2>
                  <p className="text-slate-400">
                    Please share your feedback about the interview experience
                  </p>
                </div>
                
                {uploadingRecording && (
                  <div className="mb-6 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg text-center">
                    <Loader2 className="w-5 h-5 animate-spin text-blue-400 mx-auto mb-1" />
                    <p className="text-blue-400 text-sm">Uploading recording...</p>
                  </div>
                )}
                
                {/* Star Rating */}
                <div className="mb-6">
                  <label className="block text-slate-300 text-sm font-medium mb-3">
                    How was your experience? <span className="text-red-400">*</span>
                  </label>
                  <div className="flex justify-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => setFeedbackRating(star)}
                        className="p-1 transition-transform hover:scale-110 focus:outline-none"
                        data-testid={`star-rating-${star}`}
                      >
                        <Star
                          className={`w-10 h-10 transition-colors ${
                            star <= feedbackRating
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-slate-600 hover:text-slate-500'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                  {feedbackRating > 0 && (
                    <p className="text-center text-slate-500 text-sm mt-2">
                      {feedbackRating === 1 && 'Poor'}
                      {feedbackRating === 2 && 'Fair'}
                      {feedbackRating === 3 && 'Good'}
                      {feedbackRating === 4 && 'Very Good'}
                      {feedbackRating === 5 && 'Excellent'}
                    </p>
                  )}
                </div>
                
                {/* Feedback Text */}
                <div className="mb-6">
                  <label className="block text-slate-300 text-sm font-medium mb-2">
                    Any additional feedback? (Optional)
                  </label>
                  <Textarea
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    placeholder="Share your thoughts about the interview process, questions, or any suggestions for improvement..."
                    className="min-h-[120px] bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 resize-none"
                    data-testid="feedback-textarea"
                  />
                </div>
                
                {/* Submit Button */}
                <Button 
                  onClick={submitFeedback}
                  disabled={submittingFeedback || feedbackRating === 0}
                  className="w-full bg-lime-500 hover:bg-lime-600 text-black font-medium disabled:opacity-50"
                  data-testid="submit-feedback-btn"
                >
                  {submittingFeedback ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Feedback
                    </>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];

  return (
    <div ref={containerRef} className="h-screen bg-[#050508] flex flex-col overflow-hidden">
      {/* Fullscreen Exit Prompt Overlay */}
      {showFullscreenPrompt && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center">
          <div className="bg-slate-900 border border-red-500/50 rounded-2xl p-8 max-w-md text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Fullscreen Required</h2>
            <p className="text-slate-400 mb-4">
              You have exited fullscreen mode. This has been recorded.
              Please return to fullscreen to continue your interview.
            </p>
            <p className="text-red-400 text-sm mb-6">
              Fullscreen exits: {fullscreenExitCount}
            </p>
            <Button 
              onClick={enterFullscreen}
              className="bg-lime-500 hover:bg-lime-600 text-black font-bold px-8 py-3"
              data-testid="return-fullscreen-btn"
            >
              Return to Fullscreen
            </Button>
            <p className="text-slate-500 text-xs mt-4">
              ⚠️ Repeatedly exiting fullscreen may affect your interview assessment.
            </p>
          </div>
        </div>
      )}

      {/* Top Header with Question */}
      <header className="relative z-50 bg-gradient-to-b from-[#050508] via-[#050508]/95 to-transparent flex-shrink-0">
        <div className="px-6 py-4">
          {/* Top Bar */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-lime-500 flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-black" />
              </div>
              <div>
                <span className="font-semibold text-white">Visa<span className="text-lime-400">interviews</span></span>
                <p className="text-xs text-slate-500">{universityName}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Progress Pills */}
              <div className="hidden md:flex items-center gap-1">
                {questions.map((_, idx) => (
                  <div 
                    key={idx}
                    className={`w-8 h-1.5 rounded-full transition-all ${
                      idx < currentQuestionIndex ? 'bg-lime-500' :
                      idx === currentQuestionIndex ? 'bg-lime-400' : 'bg-slate-700'
                    }`}
                  />
                ))}
              </div>

              {/* Total Time */}
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                totalTimeLeft < 60 ? 'bg-red-500/20 border border-red-500/30' : 'bg-white/5 border border-white/10'
              }`}>
                <Clock className={`w-4 h-4 ${totalTimeLeft < 60 ? 'text-red-400' : 'text-slate-400'}`} />
                <span className={`font-mono font-bold ${totalTimeLeft < 60 ? 'text-red-400' : 'text-white'}`}>
                  {formatTime(totalTimeLeft)}
                </span>
              </div>
              
              {isFullRecording && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-red-500/20 border border-red-500/30 rounded-full">
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-red-400 text-xs font-semibold tracking-wide">REC</span>
                </div>
              )}
            </div>
          </div>

          {/* Question Display - Top */}
          {(phase === 'reading' || phase === 'answering') && (
            <div className="max-w-5xl mx-auto">
              <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-5">
                <div className="flex items-center justify-between gap-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-slate-400 text-sm">Q{currentQuestionIndex + 1}/{questions.length}</span>
                      <Badge className={`text-xs px-3 py-1 ${
                        phase === 'reading' ? 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' :
                        'bg-red-500/20 text-red-400 border-red-500/30'
                      }`}>
                        {phase === 'reading' ? 'LISTEN' : 'ANSWER'}
                      </Badge>
                      {/* Audio Visualizer inline */}
                      {phase === 'reading' && settings.enable_tts && isSpeaking && (
                        <div className="flex items-center gap-2 ml-2">
                          <AudioVisualizer isActive={isSpeaking} />
                          <Volume2 className="w-3 h-3 text-cyan-400" />
                        </div>
                      )}
                    </div>
                    <p className="text-white text-lg md:text-xl font-medium leading-relaxed">
                      {currentQuestion?.question_text || currentQuestion?.text}
                    </p>
                  </div>
                  
                  {/* Timer */}
                  <div className={`flex flex-col items-center justify-center w-16 h-16 rounded-xl flex-shrink-0 ${
                    phase === 'answering' ? 'bg-red-500' : 'bg-cyan-500/20 border border-cyan-500/30'
                  }`}>
                    <span className={`font-mono text-xl font-bold ${
                      phase === 'answering' ? 'text-white' : 'text-cyan-400'
                    }`}>
                      {timeLeft}
                    </span>
                    <span className={`text-[10px] ${phase === 'answering' ? 'text-white/70' : 'text-cyan-400/70'}`}>
                      sec
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Full Screen Video Area */}
      <div className="flex-1 relative overflow-hidden bg-slate-900 flex items-center justify-center">
        {/* Video - Always visible behind overlays */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={`w-full h-full object-contain z-0 transition-opacity duration-300 ${isVideoOff ? 'opacity-0' : 'opacity-100'}`}
          style={{ transform: 'scaleX(-1)', backgroundColor: '#1a1a2e', maxHeight: '100%' }}
        />
        <canvas ref={canvasRef} className="hidden" />
        
        {/* Eye Contact Guide Overlay - Shows during answering phase */}
        {phase === 'answering' && showEyeGuide && !isVideoOff && (
          <div className="absolute inset-0 z-5 pointer-events-none">
            {/* Center target crosshair */}
            <div className="absolute top-1/3 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              {/* Outer ring */}
              <div className={`w-32 h-32 rounded-full border-2 transition-all duration-300 ${
                proctoring.currentQuestionEyeContact.percent >= 70 
                  ? 'border-green-500/60' 
                  : proctoring.currentQuestionEyeContact.percent >= 40 
                    ? 'border-yellow-500/60'
                    : 'border-red-500/60'
              }`}>
                {/* Inner crosshair */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className={`w-4 h-4 rounded-full ${
                    proctoring.currentQuestionEyeContact.percent >= 70 
                      ? 'bg-green-500/80' 
                      : proctoring.currentQuestionEyeContact.percent >= 40 
                        ? 'bg-yellow-500/80'
                        : 'bg-red-500/80 animate-pulse'
                  }`} />
                </div>
                {/* Cross lines */}
                <div className={`absolute top-1/2 left-0 right-0 h-[1px] ${
                  proctoring.currentQuestionEyeContact.percent >= 70 ? 'bg-green-500/40' : 
                  proctoring.currentQuestionEyeContact.percent >= 40 ? 'bg-yellow-500/40' : 'bg-red-500/40'
                }`} />
                <div className={`absolute left-1/2 top-0 bottom-0 w-[1px] ${
                  proctoring.currentQuestionEyeContact.percent >= 70 ? 'bg-green-500/40' : 
                  proctoring.currentQuestionEyeContact.percent >= 40 ? 'bg-yellow-500/40' : 'bg-red-500/40'
                }`} />
              </div>
            </div>
            
            {/* "Look Here" instruction */}
            <div className="absolute top-[15%] left-1/2 transform -translate-x-1/2 text-center">
              <p className={`text-sm font-medium px-3 py-1 rounded-full backdrop-blur-sm ${
                proctoring.currentQuestionEyeContact.percent >= 70 
                  ? 'bg-green-500/20 text-green-400' 
                  : proctoring.currentQuestionEyeContact.percent >= 40 
                    ? 'bg-yellow-500/20 text-yellow-400'
                    : 'bg-red-500/20 text-red-400'
              }`}>
                {proctoring.currentQuestionEyeContact.percent >= 70 
                  ? '✓ Good eye contact!' 
                  : 'Look at the camera ↓'}
              </p>
            </div>
          </div>
        )}
        
        {/* Camera Error/Loading State */}
        {!cameraStream && !cameraError && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-900 z-5">
            <div className="text-center">
              <Loader2 className="w-12 h-12 animate-spin text-lime-500 mx-auto mb-4" />
              <p className="text-slate-400">Initializing camera...</p>
            </div>
          </div>
        )}
        
        {cameraError && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-900 z-5">
            <div className="text-center">
              <VideoOff className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <p className="text-red-400 font-medium mb-2">Camera Access Required</p>
              <p className="text-slate-500 text-sm max-w-xs mx-auto">Please allow camera permissions in your browser and refresh the page</p>
            </div>
          </div>
        )}
        
        {/* Video Off State */}
        {isVideoOff && !cameraError && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#0a0a0f] z-10">
            <div className="text-center">
              <VideoOff className="w-20 h-20 text-slate-700 mx-auto mb-4" />
              <p className="text-slate-500 text-lg">Camera Off</p>
            </div>
          </div>
        )}

        {/* Warning Overlay */}
        <WarningOverlay message={warningMessage} type={warningType} />

        {/* Microphone Test Modal - Shows before countdown */}
        {phase === 'mic-test' && cameraStream && (
          <div className="absolute inset-0 flex items-center justify-center z-20" style={{ backgroundColor: 'rgba(5, 5, 8, 0.9)' }}>
            <div className="text-center backdrop-blur-xl bg-black/60 p-8 rounded-3xl border border-cyan-500/30 shadow-2xl max-w-lg mx-4">
              <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 ${
                micTestPhase === 'pending' ? 'bg-cyan-500/20 border-2 border-cyan-500/50' :
                micTestPhase === 'testing' ? 'bg-yellow-500/20 border-2 border-yellow-500/50 animate-pulse' :
                micTestPhase === 'passed' ? 'bg-green-500/20 border-2 border-green-500/50' :
                'bg-red-500/20 border-2 border-red-500/50'
              }`}>
                {micTestPhase === 'pending' && <Mic className="w-10 h-10 text-cyan-400" />}
                {micTestPhase === 'testing' && <span className="text-4xl font-bold text-yellow-400">{micTestTimer}</span>}
                {micTestPhase === 'passed' && <CheckCircle2 className="w-10 h-10 text-green-400" />}
                {micTestPhase === 'failed' && <MicOff className="w-10 h-10 text-red-400" />}
              </div>
              
              <h2 className="text-white text-2xl font-bold mb-2">
                {micTestPhase === 'pending' && 'Microphone Test'}
                {micTestPhase === 'testing' && 'Speak Now!'}
                {micTestPhase === 'passed' && 'Microphone Ready!'}
                {micTestPhase === 'failed' && 'Microphone Issue'}
              </h2>
              
              <p className="text-slate-400 mb-6">
                {micTestPhase === 'pending' && 'Let\'s make sure your microphone is working before we begin.'}
                {micTestPhase === 'testing' && 'Say something like "Hello, my name is..." to test your microphone.'}
                {micTestPhase === 'passed' && 'Great! Your microphone is working properly.'}
                {micTestPhase === 'failed' && 'We couldn\'t detect your voice. Please check your microphone settings.'}
              </p>
              
              {/* Transcript Display during test */}
              {micTestPhase === 'testing' && (
                <div className="mb-6 p-4 bg-slate-900/80 rounded-xl border border-slate-700 min-h-[80px]">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-2 h-2 rounded-full ${speechStatus === 'listening' ? 'bg-green-500 animate-pulse' : 'bg-slate-500'}`} />
                    <span className="text-xs text-slate-400">Listening...</span>
                  </div>
                  <p className="text-white text-sm">
                    {currentTranscript || interimTranscript || (
                      <span className="text-slate-500 italic">Your words will appear here...</span>
                    )}
                  </p>
                </div>
              )}
              
              {micTestPhase === 'passed' && micTestTranscript && (
                <div className="mb-6 p-4 bg-green-500/10 rounded-xl border border-green-500/30">
                  <p className="text-green-400 text-sm">
                    <span className="font-semibold">Detected:</span> "{micTestTranscript}"
                  </p>
                </div>
              )}
              
              {micTestPhase === 'failed' && (
                <div className="mb-6 p-4 bg-red-500/10 rounded-xl border border-red-500/30">
                  <p className="text-red-400 text-sm mb-2">Troubleshooting tips:</p>
                  <ul className="text-red-300/80 text-xs text-left list-disc list-inside space-y-1">
                    <li>Make sure your microphone is connected</li>
                    <li>Allow microphone permission in browser</li>
                    <li>Check if another app is using your mic</li>
                    <li>Try using Google Chrome for best results</li>
                  </ul>
                </div>
              )}
              
              <div className="flex gap-3 justify-center">
                {micTestPhase === 'pending' && (
                  <>
                    <Button
                      onClick={startMicTest}
                      className="bg-cyan-500 hover:bg-cyan-600 text-white px-6"
                    >
                      <Mic className="w-4 h-4 mr-2" />
                      Start Test
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={skipMicTest}
                      className="text-slate-400 hover:text-white"
                    >
                      Skip Test
                    </Button>
                  </>
                )}
                
                {micTestPhase === 'testing' && (
                  <Button
                    onClick={stopMicTest}
                    variant="outline"
                    className="border-yellow-500 text-yellow-400"
                  >
                    <Square className="w-4 h-4 mr-2" />
                    Stop Test
                  </Button>
                )}
                
                {micTestPhase === 'passed' && (
                  <Button
                    onClick={proceedToInterview}
                    className="bg-green-500 hover:bg-green-600 text-white px-8"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Start Interview
                  </Button>
                )}
                
                {micTestPhase === 'failed' && (
                  <>
                    <Button
                      onClick={startMicTest}
                      className="bg-cyan-500 hover:bg-cyan-600 text-white"
                    >
                      <Mic className="w-4 h-4 mr-2" />
                      Try Again
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={skipMicTest}
                      className="text-slate-400 hover:text-white"
                    >
                      Continue Anyway
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Countdown Modal - Shows before interview starts */}
        {phase === 'countdown' && cameraStream && (
          <div className="absolute inset-0 flex items-center justify-center z-20" style={{ backgroundColor: 'rgba(5, 5, 8, 0.85)' }}>
            <div className="text-center backdrop-blur-xl bg-black/50 p-12 rounded-3xl border border-lime-500/30 shadow-2xl">
              <div className="w-32 h-32 rounded-full bg-lime-500/20 border-4 border-lime-500/50 flex items-center justify-center mx-auto mb-6 animate-pulse">
                <span className="text-6xl font-bold text-lime-400">{countdownSeconds}</span>
              </div>
              <h2 className="text-white text-2xl font-bold mb-2">Get Ready!</h2>
              <p className="text-slate-400 text-lg">Your interview is about to start...</p>
              <div className="mt-6 flex items-center justify-center gap-2">
                <div className="w-2 h-2 rounded-full bg-lime-500 animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 rounded-full bg-lime-500 animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 rounded-full bg-lime-500 animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        {/* Submitted State - Center */}
        {phase === 'submitted' && (
          <div className="absolute inset-0 flex items-center justify-center z-20" style={{ backgroundColor: 'rgba(5, 5, 8, 0.6)' }}>
            <div className="text-center backdrop-blur-sm bg-black/30 p-10 rounded-3xl border border-white/10">
              {submitting ? (
                <>
                  <Loader2 className="w-16 h-16 animate-spin text-lime-500 mx-auto mb-4" />
                  <p className="text-slate-300 text-lg">Submitting your answer...</p>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <p className="text-green-400 text-lg">Answer submitted!</p>
                  <p className="text-slate-500 mt-2">Loading next question...</p>
                </>
              )}
            </div>
          </div>
        )}

        {/* Recording Indicator */}
        {isRecording && (
          <div className="absolute top-4 left-4 flex items-center gap-2 px-4 py-2 bg-red-500 rounded-full z-40">
            <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
            <span className="text-white text-sm font-semibold">Recording</span>
          </div>
        )}
      </div>

      {/* Live Speech-to-Text runs in background but is hidden from student */}
      {/* Transcription continues to work - just not displayed to avoid distraction */}

      {/* Bottom Controls */}
      <div className="relative z-50 bg-gradient-to-t from-[#050508] via-[#050508]/95 to-transparent flex-shrink-0">
        <div className="px-6 py-4">
          <div className="max-w-5xl mx-auto flex items-center justify-between">
            {/* Camera Controls */}
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMuted(!isMuted)}
                className={`w-12 h-12 rounded-full ${
                  isMuted ? 'bg-red-500/30 text-red-400 border border-red-500/30' : 'bg-white/10 text-white border border-white/10'
                }`}
              >
                {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsVideoOff(!isVideoOff)}
                className={`w-12 h-12 rounded-full ${
                  isVideoOff ? 'bg-red-500/30 text-red-400 border border-red-500/30' : 'bg-white/10 text-white border border-white/10'
                }`}
              >
                {isVideoOff ? <VideoOff className="w-5 h-5" /> : <Video className="w-5 h-5" />}
              </Button>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              {phase === 'reading' && (
                <Button 
                  onClick={startAnswering}
                  className="bg-cyan-500 hover:bg-cyan-600 text-black font-semibold px-6 py-5 rounded-xl"
                  data-testid="skip-to-answer-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Skip to Answer
                </Button>
              )}

              {phase === 'answering' && (
                <Button 
                  onClick={stopRecording}
                  className="bg-lime-500 hover:bg-lime-600 text-black font-semibold px-6 py-5 rounded-xl"
                >
                  <Square className="w-4 h-4 mr-2" />
                  Submit Answer
                </Button>
              )}
            </div>

            {/* Status Pills */}
            <div className="hidden md:flex items-center gap-2 flex-wrap">
              {fullscreenExitCount > 0 && (
                <div className="flex items-center gap-1 px-2 py-1 bg-orange-500/20 border border-orange-500/30 rounded-full">
                  <AlertTriangle className="w-3 h-3 text-orange-400" />
                  <span className="text-orange-400 text-xs">Exits: {fullscreenExitCount}</span>
                </div>
              )}
              
              {proctoring.tabSwitchCount > 0 && (
                <div className="flex items-center gap-1 px-2 py-1 bg-red-500/20 border border-red-500/30 rounded-full">
                  <AlertTriangle className="w-3 h-3 text-red-400" />
                  <span className="text-red-400 text-xs">Tabs: {proctoring.tabSwitchCount}</span>
                </div>
              )}
              
              {proctoring.faceAbsentCount > 0 && (
                <div className="flex items-center gap-1 px-2 py-1 bg-red-500/20 border border-red-500/30 rounded-full">
                  <AlertCircle className="w-3 h-3 text-red-400" />
                  <span className="text-red-400 text-xs">No Face: {proctoring.faceAbsentCount}</span>
                </div>
              )}
              
              {proctoring.multipleFacesCount > 0 && (
                <div className="flex items-center gap-1 px-2 py-1 bg-red-500/20 border border-red-500/30 rounded-full">
                  <Users className="w-3 h-3 text-red-400" />
                  <span className="text-red-400 text-xs">Multi-Face: {proctoring.multipleFacesCount}</span>
                </div>
              )}
              
              {proctoring.faceSwapEvents > 0 && (
                <div className="flex items-center gap-1 px-2 py-1 bg-red-500/20 border border-red-500/30 rounded-full">
                  <AlertCircle className="w-3 h-3 text-red-400" />
                  <span className="text-red-400 text-xs">Face Swap: {proctoring.faceSwapEvents}</span>
                </div>
              )}
              
              {/* Live Eye Contact Indicator */}
              <div className={`flex items-center gap-1 px-2 py-1 rounded-full border transition-all duration-300 ${
                proctoring.eyeContactPercent >= 80 
                  ? 'bg-green-500/20 border-green-500/30' 
                  : proctoring.eyeContactPercent >= 50 
                    ? 'bg-yellow-500/20 border-yellow-500/30'
                    : 'bg-red-500/20 border-red-500/30'
              }`}>
                <Eye className={`w-3 h-3 ${
                  proctoring.eyeContactPercent >= 80 
                    ? 'text-green-400' 
                    : proctoring.eyeContactPercent >= 50 
                      ? 'text-yellow-400'
                      : 'text-red-400'
                }`} />
                <span className={`text-xs ${
                  proctoring.eyeContactPercent >= 80 
                    ? 'text-green-400' 
                    : proctoring.eyeContactPercent >= 50 
                      ? 'text-yellow-400'
                      : 'text-red-400'
                }`}>
                  Eye: {proctoring.eyeContactPercent}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
